import React from 'react';
import { Helmet } from 'react-helmet';
import HeroSection from '@/components/HeroSection';
import ModuleGrid from '@/components/ModuleGrid';
import GlobalNavbar from '@/components/GlobalNavbar';
import Footer from '@/components/Footer';
import { Shield, Zap, Users, BarChart3 } from 'lucide-react';

const features = [
  { icon: Shield, title: 'Segurança Total', desc: 'Multiempresa com auditoria completa e rastreabilidade de cada ação.' },
  { icon: Zap, title: 'IA Integrada', desc: 'Análise inteligente de documentos, predição de prazos e assistente virtual.' },
  { icon: Users, title: 'Multi-Tenant', desc: 'Multiempresa, multifilial, multiusuário com isolamento por RLS.' },
  { icon: BarChart3, title: 'Escala Enterprise', desc: 'Preparado para 100.000 equipamentos, 10.000 usuários e 1.000 filiais.' },
];

/**
 * HomePage Component – Landing page do ecossistema PROVATIO
 */
export default function HomePage() {
  return (
    <div className="min-h-screen bg-[#F8FAFB] flex flex-col" style={{ fontFamily: "'Inter', sans-serif" }}>
      <Helmet>
        <title>PROVATIO – Plataforma Nacional de Gestão Pericial | Perícias, Laudos e Certificação</title>
        <meta name="description" content="PROVATIO: plataforma líder em gestão pericial no Brasil. Sistema integrado com IA para perícias judiciais, laudos, certificação profissional e compliance forense." />
        <meta name="keywords" content="gestão pericial, perícia judicial, laudo pericial, IA forense, documentoscopia, grafotécnica, PROVATIO" />
        <link rel="canonical" href="https://provatio.com.br/" />
        <meta property="og:title" content="PROVATIO – Plataforma Nacional de Gestão Pericial" />
        <meta property="og:description" content="Sistema integrado com IA para perícias judiciais, laudos e certificação profissional." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Navigation */}
      <GlobalNavbar />

      <main className="flex-1 w-full flex flex-col">
        {/* Hero Section */}
        <HeroSection />

        {/* Module Access Section */}
        <section className="py-16 px-4 md:px-8 max-w-7xl mx-auto w-full" id="modules-section" aria-labelledby="modules-title">
          <div className="text-center mb-14">
            <p className="text-[#FCD34D] font-bold text-xs tracking-[0.2em] uppercase mb-3">Ecossistema Completo</p>
            <h2 id="modules-title" className="text-3xl md:text-4xl font-bold text-[#193D6D] mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Módulos da Plataforma
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto text-[15px] leading-relaxed">
              Acesse as ferramentas especializadas para cada área de atuação do ecossistema pericial.
            </p>
          </div>

          <ModuleGrid />
        </section>

        {/* Features / Trust Section */}
        <section className="py-16 px-4 md:px-8 bg-gradient-to-b from-white to-[#F1F5F9]" aria-labelledby="features-title">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <p className="text-[#193D6D] font-bold text-xs tracking-[0.2em] uppercase mb-3">Por que PROVATIO?</p>
              <h2 id="features-title" className="text-3xl md:text-4xl font-bold text-[#193D6D] mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Tecnologia a Serviço da Perícia
              </h2>
            </div>
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {features.map((f, i) => (
                <div key={i} className="group flex flex-col items-center text-center p-8 rounded-2xl bg-white border border-slate-100 shadow-sm transition-all duration-300 hover:shadow-xl hover:-translate-y-1 hover:border-[#FCD34D]/30">
                  <div className="p-4 mb-5 rounded-2xl bg-[#193D6D]/5 group-hover:bg-[#193D6D] transition-all duration-300">
                    <f.icon className="w-7 h-7 text-[#193D6D] group-hover:text-[#FCD34D] transition-colors duration-300" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-bold text-[#193D6D] mb-2 text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>{f.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 md:px-8" style={{ background: 'linear-gradient(135deg, #0F2847 0%, #193D6D 50%, #2A5487 100%)' }} aria-labelledby="cta-title">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 id="cta-title" className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Pronto para transformar sua gestão pericial?
            </h2>
            <p className="text-white/70 mb-10 text-[15px] leading-relaxed max-w-xl mx-auto">
              Junte-se a centenas de peritos e escritórios que já utilizam o PROVATIO para otimizar seus processos, laudos e honorários.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/pt/system/login"
                className="px-10 py-4 bg-[#FCD34D] text-[#193D6D] font-bold rounded-full shadow-lg shadow-[#FCD34D]/20 transition-all duration-300 hover:bg-[#FBBF24] hover:shadow-xl hover:shadow-[#FCD34D]/30 hover:-translate-y-0.5 text-sm tracking-wider uppercase"
                id="cta-login">
                Começar Agora
              </a>
              <a href="mailto:suporte@provatio.com.br"
                className="px-10 py-4 border-2 border-white/30 text-white font-bold rounded-full transition-all duration-300 hover:bg-white/10 hover:border-white/60 hover:-translate-y-0.5 text-sm tracking-wider uppercase"
                id="cta-contact">
                Falar com Especialista
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}